import React, { useState, useRef, useEffect } from "react";
import { Button, Form, InputGroup, Row, Col } from "react-bootstrap";
import DateRangePicker from "../../../../common/CustomDateRangePicker";
import { getAllRushNotes } from "../../../../services/ActionStatusApi";
import Loader from "../../../../common/Loader";
import DataTable from "../../../../common/DataTable";
import { RUSH_NOTES_HEADERS } from "../../actions/Constant";
import { formatAppointmentDateTime, formatDateTime } from "../../../../utils/dateUtils";
import NotesPopUpModal from "../../../Case_Management/Case_Activities/components/NotesPopUpModal";

interface AllRushNotesDetailsProps {
    caseid?: number | null;
    hideFilters?: boolean;
}

const AllRushNotesDetails: React.FC<AllRushNotesDetailsProps> = ({ caseid = null, hideFilters = false }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
        startOfMonth,
        today,
    ]);
    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [rushNotesData, setRushNotesData] = useState<any[]>([]);

    const [notesID, setNotesID] = useState<number | null>(null);
    const [showModal, setShowModal] = useState(false);

    const toLocalISOString = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const handleDateRangeChange = (dates: [Date | null, Date | null]) => {
        setDateRange(dates);
    };

    const handleInputChange = (value: string) => {
        setSearchQuery(value);
    };

    const fetchRushNotes = async () => {
        try {
            setLoading(true);
            const [start, end] = dateRange;
            const payload = {
                searchString: searchQuery || "",
                startDate: start && !hideFilters ? toLocalISOString(start) : null,
                endDate: end && !hideFilters ? toLocalISOString(end) : null,
                caseInfoId: caseid || null,
            };

            const data = await getAllRushNotes(payload);
            const formattedData = data?.map((item: any) => ({
                ...item,
                patientName: `${item.patientName || ""} (${item.accountNo || ""}) | ${item.claimNumber || ""} | ${item.caseId || ""}`,
                createdByName: `${item.createdByName || ""}  ${formatAppointmentDateTime(item.createdDate)}`,
                isAlwaysVisible: item.visibilityStatus,
                appointmentDateTime: item.appointmentDateTime
                    ? `${formatAppointmentDateTime(item.appointmentDateTime)} | ${item.examTypeDesc || ""}`
                    : item.caseType || "",
            })) || [];
            setRushNotesData(formattedData);
        } catch (error) {
            console.error("Error fetching rush notes:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchRushNotes();
        }, 500);

        return () => clearTimeout(timer);
    }, [dateRange, searchQuery]);

    const handleResetFilters = () => {
        setDateRange([startOfMonth, today]);
        setSearchQuery("");
        if (inputRef.current) {
            inputRef.current.value = "";
        }
    };

    const handleOpenModal = (noteId: number) => {
        setNotesID(noteId);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setNotesID(null);
    };

    return (
        <div className="p-2">
            <Loader isLoading={loading} />
            {!hideFilters && (
                <Row className="mb-3 align-items-center">
                    <Col md={6}>
                        <div
                            className="date-picker-container appointment-data-range customDrodownControlDAtepicker"
                            style={{ width: "400px" }}
                        >
                            <DateRangePicker
                                label=""
                                startDate={dateRange[0] || undefined}
                                endDate={dateRange[1] || undefined}
                                onChange={handleDateRangeChange}
                                maxDate={today}
                            />
                        </div>
                    </Col>

                    <Col md={6} className="d-flex align-items-center">
                        <InputGroup className="flex-grow-1">
                            <InputGroup.Text className="px-2">
                                <span className="material-symbols-outlined text-black-50">
                                    search
                                </span>
                            </InputGroup.Text>
                            <Form.Control
                                ref={inputRef}
                                aria-label="Search"
                                placeholder="Search..."
                                onChange={(e) => handleInputChange(e.target.value.trim())}
                                onPaste={(e) => {
                                    e.preventDefault();
                                    const text = e.clipboardData.getData("text").trim();
                                    handleInputChange(text);
                                    if (inputRef.current) inputRef.current.value = text;
                                }}
                            />
                        </InputGroup>

                        <Button
                            className="d-flex align-items-center ms-2"
                            style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                padding: "10px",
                                fontSize: "22px",
                            }}
                            onClick={handleResetFilters}
                        >
                            <span className="material-icons reset_ic0">
                                restart_alt
                            </span>
                        </Button>
                    </Col>
                </Row>
            )}

            <div className="mt-3">
                <DataTable
                    headers={RUSH_NOTES_HEADERS}
                    rows={rushNotesData}
                    onRowSelect={handleOpenModal}
                />
            </div>
            <NotesPopUpModal
                show={showModal}
                onClose={handleCloseModal}
                keyId={notesID}
            />
        </div>

    );
};

export default AllRushNotesDetails;
